import type { ArticlePreview } from 'interfaces/content/articles/Articles';
import type { EventData } from 'services/Gtm/entities';
import { useEffect } from 'react';
import type { SectionTypes } from 'constants/constants';
import { useRouter } from 'next/router';
import { useScrollToElement } from 'hooks/useScrollToElement';
import Post from 'components/ContentItem/Post';
import { removeNonstandardSpaces } from 'utils/stringUtils';
import { ARTICLE_SECTION_TEST_IDS } from 'constants/testsIds/articleComponents';
import type { PagePagination } from 'utils/miscUtils/calculatePageInfo';
import * as S from './ArticleSectionStyles';

export interface AuthorArticlesPagination {
  slug: string;
  taxonomy: string;
  numPostsPerPage: number;
  totalArticles: number;
  pageNum: number;
  pageInfo: string;
  limitPagesCount?: boolean;
}

export interface AuthorArticlesProps {
  pagination: PagePagination;
  variant: string;
  trackingData?: EventData;
  showPageInfo?: boolean;
  articles?: ArticlePreview[];
  sectionType?: SectionTypes;
  sectionTitle?: string;
  className?: string;
}

const AuthorArticles = (props: AuthorArticlesProps) => {
  const { pagination, variant, articles, className } = props;
  const [executeScroll, _elRef] = useScrollToElement<HTMLHeadingElement>();
  const router = useRouter();
  const { isReady } = router;

  const isFirstPage = pagination.currentPage === 1;

  useEffect(() => {
    if (!isFirstPage && isReady) {
      executeScroll();
    }
  }, [isFirstPage, isReady, executeScroll]);

  if (!articles || articles?.length === 0) return null;

  const dataCy = variant === ARTICLE_SECTION_TEST_IDS.ARTICLES_LIST ? 'articles-list' : undefined;

  return (
    <S.Wrapper
      $variant='paginated-content-3-columns'
      className={`${className}`}
      data-cy={dataCy}
    >
      <>
        {articles?.length > 0 && (
          <div className='articles-list'>
            {articles.map((item) => (
              <Post
                authors={item.authors}
                date={item.date}
                eyebrowSectionLabel={item.eyebrowSectionLabel}
                eyebrowSectionLink={item.eyebrowSectionLink}
                key={item.title}
                image={item.image}
                title={removeNonstandardSpaces(item.title)}
                titleLink={item.titleLink}
                externalUrl={item.externalUrl}
              />
            ))}
          </div>
        )}
      </>
    </S.Wrapper>
  );
};

export default AuthorArticles;
